import { Component, DestroyRef, inject, Input, ViewChild } from '@angular/core';
import { FormControlDirective, FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { IFormInput } from '@mwe/models';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { convertDateToDisplayFormat, convertDateToISOFormat, isFormErrorVisible, isMobile } from '@mwe/utils';
import { MweDatepickerComponent } from '../../../datepicker/datepicker.component';
import dayjs from 'dayjs';
import { InputKeyDirective } from '../../form.directive';
import { BlockPasteEventDirective } from '../../directives/block-paste-event.directive';
import { NgClass } from '@angular/common';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'mwe-date-input',
  templateUrl: './date-input.component.html',
  imports: [FormsModule, InputKeyDirective, ReactiveFormsModule, BlockPasteEventDirective, NgClass, TranslateDirective, TranslatePipe],
})
export class DateInputComponent {
  private modalService = inject(NgbModal);

  @Input() parentForm: UntypedFormGroup;
  @Input() inputElem: IFormInput;
  @Input() isLoading = false;
  @ViewChild(FormControlDirective, { static: true })
  formControlDirective: FormControlDirective;

  private readonly destroyRef = inject(DestroyRef);

  get isValid(): any {
    return this.formControlDirective?.control?.valid;
  }

  get ariaDescribedBy(): string | undefined {
    const control = this.parentForm.get(this.inputElem.name);
    const hasError = isFormErrorVisible(this.inputElem, control);

    if (!!this.inputElem.helpLabelKey || hasError) {
      return `form-input-desc--${this.inputElem.name}`;
    }
    return undefined;
  }

  openDatepicker(dateInput: any, datepickerConfig: any, event: Event): void {
    event.preventDefault();
    if (convertDateToISOFormat(dateInput.value, datepickerConfig) !== dateInput.value) {
      datepickerConfig.currentValue = convertDateToISOFormat(dateInput.value, datepickerConfig);
    }
    const modalOptions: NgbModalOptions = {
      backdrop: true,
      keyboard: false,
      centered: true,
    };

    if (isMobile()) {
      modalOptions.windowClass = `animated slideInUp faster`;
    }

    const popupRef = this.modalService.open(MweDatepickerComponent, modalOptions);
    const dpComponent = popupRef.componentInstance as MweDatepickerComponent;

    dpComponent.datepickerConfig = datepickerConfig;
    dpComponent.onSubmit.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(e => {
      this.closeDatepickerPopup(e, popupRef, dateInput, datepickerConfig);
    });
    dpComponent.onReset.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.closeDatepickerPopup(null, popupRef, dateInput, null);
    });
  }

  closeDatepickerPopup(date: dayjs.Dayjs, popupRef: NgbModalRef, dateInput: any, datepickerConfig: any): void {
    if (date) {
      const isoDate = date.format('YYYY-MM-DD');
      const displayDate = convertDateToDisplayFormat(isoDate, datepickerConfig);
      const control = this.parentForm.get(dateInput.name);
      control.patchValue(displayDate);
      control.markAsDirty();
    }

    popupRef.close();
  }
}
